import React from "react";
import { Box, Grid, Stack } from "@mui/material";
import styled from "styled-components";
import { Edit } from "@mui/icons-material";
import WorkingHoursInfoCard from "src/scenes/profile/working-hours-info-card";
import LeaderboardSettingsCard from "src/scenes/profile/leaderboard-settings-card";
import { Helmet } from "react-helmet";
import SkillTree from "./skill-tree";
import { useAuth } from "../../components/providers/auth-context";
import useProfileApi from "./use-profile-api";
import ShowWithSkeleton from "../../utils/show-with-skeleton";
import OrganisationalInfoCard from "./organisational-info-card";
import Picture from "../../components/picture";
import request from "../../utils/request";
import ContainedTextField from "../../components/form-fields/contained-text-field";
import MfaSettingsCard from "./mfa-settings-card";

const Email = styled.p`
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.palette.primary.main};
    margin: 0;
`;

const SectionTitle = styled.span`
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const Profile = () => {
    const { user, updateUser } = useAuth();
    const profile = useProfileApi();
    const showSkills = false;

    return (
        <>
            <Helmet>
                <title>Adadot - Profile</title>
            </Helmet>
            <Box width="100%" maxWidth="1000px">
                <Grid container spacing={5} direction="column">
                    <Grid container item spacing={3} direction="row">
                        <Grid item>
                            <Picture
                                picture={user.picture}
                                requestUrl="/api/users/me/picture"
                                onSuccess={() => updateUser()}
                            />
                        </Grid>
                        <Grid
                            container
                            item
                            direction="column"
                            width="auto"
                            justifyContent="center"
                        >
                            <Grid item>
                                <ContainedTextField
                                    initialValue={user.name && user.name.givenName}
                                    refetch={updateUser}
                                    placeholderText="Enter Your First Name"
                                    requestFormatter={(value) => {
                                        return request("/api/users/me", {
                                            method: "POST",
                                            body: JSON.stringify({ name: { givenName: value } }),
                                        });
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <ContainedTextField
                                    initialValue={user.name && user.name.familyName}
                                    refetch={updateUser}
                                    placeholderText="Enter Your Last Name"
                                    requestFormatter={(value) => {
                                        return request("/api/users/me", {
                                            method: "POST",
                                            body: JSON.stringify({ name: { familyName: value } }),
                                        });
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <Email>{user.emails && user.emails.length && user.emails[0]}</Email>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid container item spacing={3}>
                        <Grid item sm={12} md={showSkills ? 6 : 12}>
                            <OrganisationalInfoCard user={user} />
                        </Grid>
                        <Grid item sm={12} md={showSkills ? 6 : 12}>
                            <LeaderboardSettingsCard user={user} updateUser={updateUser} />
                        </Grid>
                        <Grid item sm={12} md={showSkills ? 6 : 12}>
                            <WorkingHoursInfoCard user={user} updateUser={updateUser} />
                        </Grid>
                        <Grid item sm={12} md={showSkills ? 6 : 12} mb={8}>
                            <MfaSettingsCard user={user} updateUser={updateUser} />
                        </Grid>
                        {showSkills && (
                            <Grid item sm={12} md={6}>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    pl={2}
                                    pr={2}
                                    pt={1}
                                    pb={1}
                                >
                                    <SectionTitle>Skills</SectionTitle>
                                    <Edit
                                        color="secondary"
                                        fontSize="small"
                                        style={{ cursor: "pointer" }}
                                    />
                                </Stack>

                                <ShowWithSkeleton fetchAction={profile}>
                                    <SkillTree profileData={user} />
                                </ShowWithSkeleton>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default Profile;
